// React and PropTypes
import React, { memo, useRef } from "react";
import PropTypes from "prop-types";

// Third-party libraries
import _ from "lodash";
import classNames from "classnames";

// Toddle Design System components
import { ChevronRightOutlined } from "@toddle-edu/ds-icons";
import { Badge, Tooltip } from "@toddle-edu/ds-web";

// Internal components
import { KeyboardNavigableList } from "UIComponents/KeyboardNavigableList";

// Styles
import classes from "./FilterList.scss";

const buttonClasses = classNames("text-body", classes.button);

/**
 * @component FilterList
 * @description Renders a list of filter categories that can be selected.
 * Uses KeyboardNavigableList for accessibility.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.options - Array of filter category options, including `id`, `label`, and `filtersAppliedCount`.
 * @param {string|number} props.currentFilterType - The ID of the currently selected filter category.
 * @param {Function} props.setCurrentFilterType - Callback function to set the current filter type.
 * @param {string} props.testId - Base test ID for targeting elements.
 *
 * @example
 * const options = [
 *   { id: 'grades', label: 'Grades', filtersAppliedCount: 2 },
 *   { id: 'subjects', label: 'Subjects', filtersAppliedCount: 0 },
 * ];
 * const current = 'grades';
 * const setCurrent = (id) => console.log('Selected:', id);
 *
 * <FilterList
 *   options={options}
 *   currentFilterType={current}
 *   setCurrentFilterType={setCurrent}
 *   testId="my-filter"
 * />
 */
const FilterList = props => {
  const { options, currentFilterType, setCurrentFilterType, testId } = props;
  const containerRef = useRef(null);

  return (
    <KeyboardNavigableList
      listContainerRef={containerRef}
      shouldPreventDefault // Prevent page scroll on arrow keys
      listRole="listbox" // Role for the container
      listItemRole="option" // Role for each selectable item
    >
      <div className={classes.container} ref={containerRef}>
        {_.map(options, option => {
          // Default filtersAppliedCount to 0 if not provided
          const { id, label, filtersAppliedCount = 0 } = option;
          const isSelected = currentFilterType === id;

          return (
            <button
              key={id}
              onClick={() => setCurrentFilterType(id)}
              role="option"
              data-selected={isSelected}
              aria-selected={isSelected}
              className={buttonClasses}
              data-test-id={`${testId}-filtersList-${id}`}
              aria-labelledby={`filterList-${id}`}
            >
              <Tooltip tooltip={label}>
                <span className={classes.btnLabel} id={`filterList-${id}`}>
                  {label}
                </span>
              </Tooltip>
              <div className={classes.rightContainer}>
                <Badge
                  value={filtersAppliedCount}
                  type="numeric"
                  shape="circle"
                  variant="dark"
                  aria-hidden="true"
                />
                <ChevronRightOutlined aria-hidden="true" />
              </div>
            </button>
          );
        })}
      </div>
    </KeyboardNavigableList>
  );
};

FilterList.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      filtersAppliedCount: PropTypes.number,
    })
  ).isRequired,
  currentFilterType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setCurrentFilterType: PropTypes.func.isRequired,
  testId: PropTypes.string.isRequired,
};

export default memo(FilterList);
